import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { GoogleLogo, ArrowRight, Leaf, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Auth({ mode = "login" }) {
  const navigate = useNavigate();
  const { login, register, formatApiErrorDetail } = useAuth();
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Strip invisible/zero-width chars that Chrome autofill sometimes injects
      const cleanEmail = email
        .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "")
        .trim()
        .toLowerCase();
      const cleanPassword = password.replace(/[\u200B-\u200D\uFEFF]/g, "");
      if (isLogin) {
        await login(cleanEmail, cleanPassword);
      } else {
        await register(cleanEmail, cleanPassword, name);
      }
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message =
        formatApiErrorDetail(detail) ||
        (err.message?.includes("Network") ? "Network problem — check your connection." : "Something went wrong. Please try again.");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex justify-center">
      <div className="w-full max-w-md min-h-screen flex flex-col px-6 py-10 sm:px-8 sm:py-14">
        {/* Brand */}
        <div className="flex items-center gap-2.5">
          <span className="w-10 h-10 rounded-full bg-[#2B422F] flex items-center justify-center">
            <Leaf size={20} weight="fill" className="text-[#E6D4C4]" />
          </span>
          <div>
            <div className="font-serif-display text-2xl leading-none text-[#1A2421]">Tally</div>
            <div className="label-eyebrow mt-1">Carb Counter</div>
          </div>
        </div>

        {/* Heading */}
        <div className="mt-14">
          <span className="label-eyebrow">{isLogin ? "Welcome back" : "Create account"}</span>
          <h1 className="font-serif-display text-4xl tracking-tight text-[#1A2421] mt-2">
            {isLogin ? "Sign in" : "Begin tracking"}
          </h1>
          <p className="text-sm text-[#5C6B62] mt-2">
            {isLogin ? "Pick up where you left off." : "A carb tracker for diabetes management."}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="auth-form">
          {!isLogin && (
            <div>
              <label className="label-eyebrow block mb-2">Name</label>
              <input
                className="input-organic"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="auth-name-input"
                placeholder="Your name"
              />
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-2">Email</label>
            <input
              className="input-organic"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="auth-email-input"
              placeholder="you@example.com"
              inputMode="email"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Password</label>
            <div className="relative">
              <input
                className="input-organic pr-12"
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input"
                placeholder="At least 6 characters"
                autoComplete={isLogin ? "current-password" : "new-password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5C6B62] hover:text-[#1A2421]"
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="auth-password-toggle"
                tabIndex={-1}
              >
                {showPassword ? <EyeSlash size={20} weight="regular" /> : <Eye size={20} weight="regular" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="auth-submit-button"
          >
            {submitting ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
            <ArrowRight size={16} weight="bold" />
          </button>

          {isLogin && (
            <div className="text-center -mt-1">
              <a
                href="/forgot-password"
                onClick={(e) => { e.preventDefault(); navigate("/forgot-password"); }}
                className="text-xs text-[#5C6B62] hover:text-[#1A2421] underline underline-offset-4"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </a>
            </div>
          )}
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E6E3D8]" /></div>
          <span className="relative bg-[#F9F8F6] px-3 text-xs uppercase tracking-[0.18em] text-[#5C6B62]">or</span>
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          className="btn-outline w-full flex items-center justify-center gap-2"
          data-testid="google-login-button"
        >
          <GoogleLogo size={18} weight="bold" />
          Continue with Google
        </button>

        <p className="mt-7 text-sm text-[#5C6B62] text-center">
          {isLogin ? "New here?" : "Already have an account?"}{" "}
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-[#1A2421] underline underline-offset-4 hover:text-[#2B422F]"
            data-testid="auth-toggle-button"
          >
            {isLogin ? "Create an account" : "Sign in"}
          </button>
        </p>

        {isLogin && (
          <div className="mt-6 rounded-2xl bg-white border border-[#E6E3D8] p-4" data-testid="login-help-panel">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#5C6B62]">Trouble signing in?</div>
            <p className="text-sm text-[#1A2421] mt-2 leading-relaxed">
              Don&apos;t worry — your subscription and meal history are safe. We&apos;ll help you back in.
            </p>
            <a
              href="mailto:tallycarbcounter@gmail.com?subject=Tally%20%C2%B7%20Help%20signing%20in&body=Hi%2C%20I%27m%20having%20trouble%20signing%20into%20Tally.%0A%0AMy%20account%20email%20is%3A%0A%0AWhat%27s%20happening%3A%0A"
              className="btn-outline w-full mt-3 inline-flex items-center justify-center gap-2 !py-2 text-sm"
              data-testid="login-contact-support"
            >
              Contact support
            </a>
            <p className="text-[11px] text-[#5C6B62] mt-2 text-center">
              We usually respond within 24 hours.
            </p>
          </div>
        )}

        <div className="mt-auto pt-10">
          <p className="text-xs text-[#5C6B62] text-center">
            Demo: <code className="px-1.5 py-0.5 bg-[#F0EFE9] rounded">demo@carbcounter.app</code> /{" "}
            <code className="px-1.5 py-0.5 bg-[#F0EFE9] rounded">Demo@123</code>
          </p>
          <p className="mt-3 text-[10px] text-[#5C6B62]/70 text-center">For informational use. Verify with your care team.</p>
        </div>
      </div>
    </div>
  );
}
